import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import { capabilitiesFromUser } from '../utils/solutionCapabilities.js';
import './FrictionMap.css';
import './Calculator.css';
import './AnalyticalBoards.css';

function climateOf(band, score) {
    if (band === 'alto' || score >= 70) {
        return {
            emoji: '🌧️',
            title: 'La manada pide compañía',
            tone: '#b91c1c',
            tip: 'Hoy conviene una conversación breve, sin culpas.',
        };
    }
    if (band === 'medio' || score >= 40) {
        return {
            emoji: '⛅',
            title: 'Hay nubes: mirar con calma',
            tone: '#b45309',
            tip: 'Un ritual corto de equipo ayuda a recuperar sintonía.',
        };
    }
    return {
        emoji: '☀️',
        title: 'La manada está en sintonía',
        tone: '#047857',
        tip: 'Mantén el ritual de jornada y la escucha cercana.',
    };
}

function areaKey(id) {
    return id == null ? 'sin-area' : String(id);
}

const STEPS = [
    { id: 'mirar', n: 1, label: 'Mirar', hint: 'Ver el pulso' },
    { id: 'modelos', n: 2, label: 'Modelos', hint: 'Leer el mapa' },
    { id: 'acompanar', n: 3, label: 'Acompañar', hint: 'Elegir un gesto' },
    { id: 'registrar', n: 4, label: 'Registrar', hint: 'Cerrar el ciclo' },
];

/**
 * Estado Funcional — tablero cálido de gestión (metodología ALINEA / ritmo CID).
 */
export default function AnalyticalBoards() {
    const { user } = useAuth();
    const { tenantId } = useTenantScope();
    const caps = capabilitiesFromUser(user);
    const canManage = ['admin', 'superadmin'].includes(user?.role);

    const [flags, setFlags] = useState(null);
    const [cip, setCip] = useState(null);
    const [manada, setManada] = useState(null);
    const [preventive, setPreventive] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [proposing, setProposing] = useState(false);
    const [savingFlags, setSavingFlags] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [step, setStep] = useState('mirar');
    const [modalCase, setModalCase] = useState(null);
    const [modalNote, setModalNote] = useState('');
    const [savingModal, setSavingModal] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);
    const [suite, setSuite] = useState(null);
    const [maDetail, setMaDetail] = useState(null);
    const [maLoading, setMaLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const st = await api.getFunctionalStateStatus();
            const f = st?.data || {};
            setFlags(f);
            const tasks = [];
            if (f.cipEnabled) tasks.push(api.getCipSummary().then((r) => setCip(r.data)));
            else setCip(null);
            if (f.manadaAnalyticsEnabled) tasks.push(api.getManadaDynamics().then((r) => setManada(r.data)));
            else setManada(null);
            if (f.preventiveEngineEnabled) tasks.push(api.getPreventiveQueue().then((r) => setPreventive(r.data)));
            else setPreventive(null);
            tasks.push(api.getAnalyticalSuite().then((r) => setSuite(r.data)).catch(() => setSuite(null)));
            await Promise.all(tasks);
        } catch (err) {
            setError(err.message || 'No se pudo cargar');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [tenantId, reload]);

    const packs = useMemo(() => {
        const byId = new Map();
        for (const p of manada?.packs || []) {
            byId.set(areaKey(p.areaId), {
                areaId: p.areaId,
                name: p.areaName,
                cohesion: Number(p.cohesionScore) || 0,
                sombra: Math.round((p.shadowRatio || 0) * 100),
                reading: p.reading,
                cipScore: null,
                band: 'bajo',
            });
        }
        for (const a of cip?.areas || []) {
            const k = areaKey(a.areaId);
            const prev = byId.get(k) || {
                areaId: a.areaId,
                name: a.areaName,
                cohesion: 50,
                sombra: 0,
                reading: null,
            };
            byId.set(k, {
                ...prev,
                name: a.areaName || prev.name,
                cipScore: a.cipScore,
                band: a.band || prev.band,
                reading: prev.reading || a.recommendation,
                recommendation: a.recommendation,
            });
        }
        return [...byId.values()].sort((a, b) => (b.cipScore ?? b.sombra) - (a.cipScore ?? a.sombra));
    }, [cip, manada]);

    const chartData = packs.map((p) => ({
        name: (p.name || 'Área').slice(0, 12),
        sintonía: p.cohesion,
        sombra: p.sombra,
        fullName: p.name,
        areaId: p.areaId,
    }));

    const cases = preventive?.cases || [];
    const climate = climateOf(cip?.organization?.band, cip?.organization?.cipScore ?? 0);
    const noneOn =
        !flags?.cipEnabled &&
        !flags?.manadaAnalyticsEnabled &&
        !flags?.preventiveEngineEnabled &&
        !flags?.modelosAnaliticosEnabled;

    async function setModule(patch) {
        if (!canManage) return;
        setSavingFlags(true);
        setError('');
        try {
            const next = {
                cipEnabled: flags?.cipEnabled,
                manadaAnalyticsEnabled: flags?.manadaAnalyticsEnabled,
                preventiveEngineEnabled: flags?.preventiveEngineEnabled,
                modelosAnaliticosEnabled: flags?.modelosAnaliticosEnabled,
                efCoreEnabled: flags?.efCoreEnabled,
                efVoiceEnabled: flags?.efVoiceEnabled,
                dashboardsEfEnabled: flags?.dashboardsEfEnabled,
                ...patch,
            };
            if (
                next.cipEnabled ||
                next.manadaAnalyticsEnabled ||
                next.preventiveEngineEnabled ||
                next.modelosAnaliticosEnabled
            ) {
                next.dashboardsEfEnabled = true;
            }
            await api.putFunctionalFlags(next);
            await reload();
        } catch (err) {
            setError(err.message || 'No se pudo guardar');
        } finally {
            setSavingFlags(false);
        }
    }

    async function openModel(key) {
        if (key === 'cip') {
            setStep('mirar');
            return;
        }
        if (key === 'manada') {
            setStep('mirar');
            return;
        }
        if (key === 'preventiva') {
            setStep('acompanar');
            return;
        }
        if (key === 'cid') {
            window.location.assign('/calculator');
            return;
        }
        const loaders = {
            bienestar: api.getBienestarSummary,
            riesgo: api.getRiesgoSummary,
            liderazgo: api.getLiderazgoSummary,
            adaptacion: api.getAdaptacionSummary,
        };
        if (!loaders[key]) return;
        if (!flags?.modelosAnaliticosEnabled) {
            setError('Activa “Modelos” en Ajustar lecturas para ver esta lectura.');
            return;
        }
        setMaLoading(true);
        setError('');
        try {
            const r = await loaders[key]();
            setMaDetail(r.data);
            setStep('modelos');
        } catch (err) {
            setError(err.message || 'No se pudo cargar el modelo');
        } finally {
            setMaLoading(false);
        }
    }

    async function prepareActions() {
        if (!flags?.preventiveEngineEnabled) {
            setError('Activa la cola de acompañamiento en “Ajustar lecturas”.');
            return;
        }
        setProposing(true);
        setError('');
        try {
            await api.proposePreventive();
            const r = await api.getPreventiveQueue();
            setPreventive(r.data);
            setStep('acompanar');
        } catch (err) {
            setError(err.message || 'No se pudieron preparar acompañamientos');
        } finally {
            setProposing(false);
        }
    }

    function openRegister(c) {
        setModalCase(c);
        setModalNote('');
        setStep('registrar');
    }

    async function saveRegister() {
        const note = modalNote.trim();
        if (!modalCase || !note) return;
        setSavingModal(true);
        setError('');
        try {
            await api.preventiveFollowUp(modalCase.alertId, {
                actionTaken: note,
                outcome: 'registered',
            });
            setModalCase(null);
            setModalNote('');
            const r = await api.getPreventiveQueue();
            setPreventive(r.data);
            setStep(cases.length > 1 ? 'acompanar' : 'mirar');
        } catch (err) {
            setError(err.message || 'No se pudo guardar el seguimiento');
        } finally {
            setSavingModal(false);
        }
    }

    if (!caps.showEstadoFuncional) {
        return <Navigate to="/" replace />;
    }

    if (loading) {
        return (
            <div className="friction-map-page ef-board">
                <p className="text-muted">Escuchando a la manada…</p>
            </div>
        );
    }

    return (
        <div className="friction-map-page ef-board">
            {/* Héro CID-like */}
            <div
                className="glass-card calc-dash-hero ef-hero"
                style={{ borderLeft: `5px solid ${climate.tone}` }}
            >
                <div className="ef-hero-top">
                    <div>
                        <p className="ef-kicker">Estado Funcional</p>
                        <h1>
                            <span className="ef-emoji" aria-hidden>
                                {climate.emoji}
                            </span>{' '}
                            {climate.title}
                        </h1>
                        <p className="ef-lead">
                            Es el pulso vivo de la organización: cómo está la manada y qué gesto humano
                            toca hoy. No es un juicio a personas ni reemplaza el CID o las alertas.
                        </p>
                    </div>
                    <div className="ef-hero-cta">
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={proposing || noneOn}
                            onClick={prepareActions}
                        >
                            {proposing ? 'Preparando…' : '¿Qué acompañamos hoy?'}
                        </button>
                        <Link to="/alerts" className="btn btn-secondary">
                            Ver alertas
                        </Link>
                    </div>
                </div>
                <p className="ef-tip">{climate.tip}</p>
            </div>

            {/* Metodología interactiva */}
            <nav className="ef-steps" aria-label="Camino del Estado Funcional">
                {STEPS.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        className={`ef-step ${step === s.id ? 'is-active' : ''}`}
                        onClick={() => setStep(s.id)}
                        disabled={noneOn}
                    >
                        <span className="ef-step-n">{s.n}</span>
                        <span className="ef-step-label">{s.label}</span>
                        <span className="ef-step-hint">{s.hint}</span>
                    </button>
                ))}
            </nav>

            {error && (
                <div className="friction-alert" role="alert">
                    <span>{error}</span>
                </div>
            )}

            {canManage && (
                <div className="ef-admin-bar glass-card">
                    <span>¿Encender u ocultar partes del tablero?</span>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowAdmin((v) => !v)}>
                        {showAdmin ? 'Listo' : 'Ajustar lecturas'}
                    </button>
                    {showAdmin && (
                        <div className="ef-admin-grid">
                            {[
                                ['cipEnabled', 'Señales de cuidado'],
                                ['manadaAnalyticsEnabled', 'Pulso de manadas'],
                                ['preventiveEngineEnabled', 'Cola de acompañamiento'],
                                ['modelosAnaliticosEnabled', 'Modelos (bienestar, riesgo, guía, adaptación, CID)'],
                            ].map(([key, label]) => (
                                <label key={key}>
                                    <input
                                        type="checkbox"
                                        checked={!!flags?.[key]}
                                        disabled={savingFlags}
                                        onChange={(e) => setModule({ [key]: e.target.checked })}
                                    />
                                    {label}
                                </label>
                            ))}
                            <label>
                                <input
                                    type="checkbox"
                                    checked={!!flags?.efCoreEnabled}
                                    disabled={savingFlags}
                                    onChange={(e) =>
                                        setModule({
                                            efCoreEnabled: e.target.checked,
                                            efVoiceEnabled: e.target.checked,
                                        })
                                    }
                                />
                                Foto inicial en registro
                            </label>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={savingFlags}
                                onClick={() =>
                                    setModule({
                                        efCoreEnabled: true,
                                        efVoiceEnabled: true,
                                        dashboardsEfEnabled: true,
                                        cipEnabled: true,
                                        manadaAnalyticsEnabled: true,
                                        preventiveEngineEnabled: true,
                                        modelosAnaliticosEnabled: true,
                                    })
                                }
                            >
                                Encender piloto
                            </button>
                        </div>
                    )}
                </div>
            )}

            {noneOn ? (
                <div className="glass-card" style={{ padding: '1.5rem' }}>
                    <p style={{ margin: 0, lineHeight: 1.55 }}>
                        El Estado Funcional está en silencio. Actívalo con <strong>Ajustar lecturas</strong>.
                        Mientras tanto, jornada y CID siguen igual.
                    </p>
                </div>
            ) : (
                <>
                    {/* KPIs clicables */}
                    <div className="grid-3 ef-kpis">
                        <button
                            type="button"
                            className="glass-card stat-card ef-kpi"
                            onClick={() => setStep('mirar')}
                        >
                            <h4>🌤️ Clima</h4>
                            <p className="stat-value" style={{ color: climate.tone, fontSize: '1.25rem' }}>
                                {climate.emoji} Hoy
                            </p>
                            <p className="text-xs mt-sm opacity-80">Toca para mirar el pulso</p>
                        </button>
                        <button
                            type="button"
                            className="glass-card stat-card ef-kpi"
                            onClick={() => setStep('modelos')}
                        >
                            <h4>📐 Modelos</h4>
                            <p className="stat-value">
                                {(suite?.models || []).filter((m) => m.enabled).length || 0}
                            </p>
                            <p className="text-xs mt-sm opacity-80">Lecturas Obra IV activas</p>
                        </button>
                        <button
                            type="button"
                            className="glass-card stat-card ef-kpi"
                            onClick={() => setStep('acompanar')}
                        >
                            <h4>🤝 Por acompañar</h4>
                            <p className="stat-value text-orange">{cases.length}</p>
                            <p className="text-xs mt-sm opacity-80">Gestos abiertos hoy</p>
                        </button>
                    </div>

                    {step === 'mirar' && (
                        <section className="ef-panel stagger">
                            <div className="glass-card mb-lg calc-dash-chart-card">
                                <h3>Sintonía y sombra por manada</h3>
                                <p className="calc-discrete-hint mb-md">
                                    Barras verdes: sintonía · Barras ámbar: sombra en la jornada. Toca una barra
                                    o una tarjeta para conversar.
                                </p>
                                {chartData.length === 0 ? (
                                    <p className="text-muted">
                                        Aún faltan check-ins. Cuando la manada registre su día, aquí verás el
                                        pulso.
                                    </p>
                                ) : (
                                    <div className="calc-dash-chart-box" style={{ height: 280 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={chartData}
                                                margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                                                onClick={(state) => {
                                                    const i = state?.activeTooltipIndex;
                                                    if (i == null || !packs[i]) return;
                                                    setSelectedPack(packs[i]);
                                                }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                                                <Tooltip
                                                    formatter={(value, name) => [
                                                        value,
                                                        name === 'sintonía' ? 'Sintonía' : 'Sombra %',
                                                    ]}
                                                    labelFormatter={(_, payload) =>
                                                        payload?.[0]?.payload?.fullName || ''
                                                    }
                                                />
                                                <Bar dataKey="sintonía" radius={[4, 4, 0, 0]} maxBarSize={36}>
                                                    {chartData.map((_, i) => (
                                                        <Cell key={`s-${i}`} fill="#1d5b79" />
                                                    ))}
                                                </Bar>
                                                <Bar dataKey="sombra" radius={[4, 4, 0, 0]} maxBarSize={36}>
                                                    {chartData.map((_, i) => (
                                                        <Cell key={`o-${i}`} fill="#d97706" />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>

                            <div className="ef-pack-grid">
                                {packs.map((p) => {
                                    const c = climateOf(p.band, p.cipScore ?? p.sombra);
                                    const active = selectedPack && areaKey(selectedPack.areaId) === areaKey(p.areaId);
                                    return (
                                        <button
                                            type="button"
                                            key={areaKey(p.areaId)}
                                            className={`glass-card ef-pack-card ${active ? 'is-selected' : ''}`}
                                            style={{ borderLeft: `4px solid ${c.tone}` }}
                                            onClick={() => setSelectedPack(p)}
                                        >
                                            <div className="ef-pack-head">
                                                <strong>
                                                    {c.emoji} {p.name}
                                                </strong>
                                                <span style={{ color: c.tone }}>{c.title.split(':')[0]}</span>
                                            </div>
                                            <p>{p.reading || p.recommendation || c.tip}</p>
                                            <p className="ef-pack-meta">
                                                Sintonía {p.cohesion}
                                                {p.sombra != null ? ` · sombra ${p.sombra}%` : ''}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>

                            {selectedPack && (
                                <div className="glass-card ef-selected-action">
                                    <div>
                                        <h3>Conversar con {selectedPack.name}</h3>
                                        <p>
                                            {selectedPack.reading ||
                                                selectedPack.recommendation ||
                                                'Un gesto breve de equipo suele bastar.'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={async () => {
                                            await prepareActions();
                                            setStep('acompanar');
                                        }}
                                    >
                                        Pasar a acompañar
                                    </button>
                                </div>
                            )}

                            {!selectedPack && packs.length > 0 && (
                                <p className="text-muted ef-hint-center">
                                    Elige una manada arriba o pulsa <strong>¿Qué acompañamos hoy?</strong>
                                </p>
                            )}
                        </section>
                    )}

                    {step === 'modelos' && (
                        <section className="ef-panel stagger">
                            <div className="glass-card mb-lg" style={{ padding: '1.25rem' }}>
                                <h3 style={{ marginTop: 0 }}>Modelos Analíticos</h3>
                                <p className="text-muted" style={{ marginBottom: '1rem' }}>
                                    Cada modelo lee el Estado Funcional desde un ángulo (Obra IV). El CID
                                    sigue siendo la Calculadora: aquí solo lo enlazamos.
                                </p>
                                {!flags?.modelosAnaliticosEnabled && (
                                    <p className="text-muted">
                                        Los modelos extendidos están apagados. Actívalos en{' '}
                                        <strong>Ajustar lecturas</strong>.
                                    </p>
                                )}
                                <div className="ef-pack-grid">
                                    {(suite?.models || [])
                                        .filter((m) =>
                                            ['bienestar', 'riesgo', 'liderazgo', 'adaptacion', 'cid', 'cip', 'manada'].includes(
                                                m.key
                                            )
                                        )
                                        .map((m) => (
                                            <button
                                                type="button"
                                                key={m.key}
                                                className="glass-card ef-pack-card"
                                                onClick={() => openModel(m.key)}
                                                disabled={maLoading}
                                            >
                                                <div className="ef-pack-head">
                                                    <strong>{m.humanName}</strong>
                                                    <span>{m.enabled ? 'Listo' : 'Apagado'}</span>
                                                </div>
                                                <p>{m.name === 'CID' ? 'Ir a la Calculadora CID' : `Abrir ${m.name}`}</p>
                                            </button>
                                        ))}
                                </div>
                                {suite?.dimensions?.length > 0 && (
                                    <div style={{ marginTop: '1.25rem' }}>
                                        <h4 style={{ margin: '0 0 0.5rem' }}>Dimensiones del Estado Funcional</h4>
                                        <div className="ef-dim-chips">
                                            {suite.dimensions.map((d) => (
                                                <span key={d.key} className="ef-dim-chip" title={d.source}>
                                                    {d.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {maDetail && (
                                <div className="glass-card calc-dash-chart-card" style={{ padding: '1.25rem' }}>
                                    <h3 style={{ textTransform: 'capitalize' }}>
                                        {maDetail.model === 'bienestar'
                                            ? 'Bienestar colectivo'
                                            : maDetail.model === 'riesgo'
                                              ? 'Riesgo de desconexión'
                                              : maDetail.model === 'liderazgo'
                                                ? 'Guía y fricción'
                                                : maDetail.model === 'adaptacion'
                                                  ? 'Capacidad de cambio'
                                                  : maDetail.model}
                                    </h3>
                                    <p className="text-muted">{maDetail.disclaimer}</p>
                                    {maDetail.organization && (
                                        <p style={{ fontWeight: 600, margin: '0.75rem 0' }}>
                                            Organización:{' '}
                                            {maDetail.organization.wellbeingScore ??
                                                maDetail.organization.riskScore ??
                                                maDetail.organization.leadershipScore ??
                                                maDetail.organization.adaptationScore ??
                                                '—'}
                                            {maDetail.organization.bandLabel
                                                ? ` · ${maDetail.organization.bandLabel}`
                                                : ''}
                                        </p>
                                    )}
                                    <div className="ef-action-list">
                                        {(maDetail.areas || []).slice(0, 8).map((a) => (
                                            <div key={String(a.areaId)} className="glass-card ef-action-card">
                                                <div className="ef-action-head">
                                                    <strong>{a.areaName}</strong>
                                                    <span>{a.bandLabel}</span>
                                                </div>
                                                <p>{a.reading}</p>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={() => setStep('acompanar')}
                                                >
                                                    Pasar a acompañar
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {step === 'acompanar' && (
                        <section className="ef-panel stagger">
                            <div className="glass-card mb-lg" style={{ padding: '1.25rem' }}>
                                <h3 style={{ marginTop: 0 }}>Gestos de acompañamiento</h3>
                                <p className="text-muted" style={{ marginBottom: '1rem' }}>
                                    Elige uno, abre el detalle y registra qué hicieron juntos. Así cierras el
                                    ciclo del Estado Funcional.
                                </p>
                                {cases.length === 0 ? (
                                    <div className="ef-empty">
                                        <p>Todavía no hay gestos preparados.</p>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            disabled={proposing}
                                            onClick={prepareActions}
                                        >
                                            {proposing ? 'Preparando…' : 'Preparar desde las señales'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="ef-action-list">
                                        {cases.map((c) => {
                                            const title = (c.title || 'Manada')
                                                .replace(/^Preventivo P1 · /, '')
                                                .replace(/^Preventivo P1 /, '');
                                            return (
                                                <div key={c.alertId} className="glass-card ef-action-card">
                                                    <div className="ef-action-head">
                                                        <strong>🤝 {title}</strong>
                                                        <span>
                                                            {c.level === 'red'
                                                                ? 'Prioridad alta'
                                                                : c.level === 'yellow'
                                                                  ? 'Prioridad media'
                                                                  : 'Suave'}
                                                        </span>
                                                    </div>
                                                    <p>
                                                        {c.pipeline?.stages?.recommendation ||
                                                            c.description ||
                                                            'Conversación breve de seguridad y cuidado.'}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={() => openRegister(c)}
                                                    >
                                                        Acompañar y registrar
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <button type="button" className="btn btn-secondary" onClick={() => setStep('mirar')}>
                                ← Volver a mirar
                            </button>
                        </section>
                    )}

                    {step === 'registrar' && !modalCase && (
                        <section className="ef-panel">
                            <div className="glass-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
                                <p>Elige un acompañamiento en el paso anterior para registrar el seguimiento.</p>
                                <button type="button" className="btn btn-primary" onClick={() => setStep('acompanar')}>
                                    Ir a acompañar
                                </button>
                            </div>
                        </section>
                    )}
                </>
            )}

            {modalCase && (
                <div className="modal-overlay ef-modal-overlay" role="presentation" onClick={() => setModalCase(null)}>
                    <div
                        className="modal-content glass-card ef-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="ef-modal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="ef-kicker">Registrar seguimiento</p>
                        <h2 id="ef-modal-title">
                            {(modalCase.title || '')
                                .replace(/^Preventivo P1 · /, 'Manada ')
                                .replace(/^Preventivo P1 /, 'Manada ')}
                        </h2>
                        <p className="modal-sub">Escucha · Acuerda · Acompaña</p>
                        <div className="ef-modal-steps">
                            <div className="step">
                                <strong>1</strong> Escuchar cómo llegó la manada a la jornada.
                            </div>
                            <div className="step">
                                <strong>2</strong> Acordar un gesto concreto (pausa, ayuda, claridad).
                            </div>
                            <div className="step">
                                <strong>3</strong> Dejar anotado qué hicieron — cierra el ciclo.
                            </div>
                        </div>
                        <label htmlFor="ef-follow-note">¿Qué acompañamiento hicieron?</label>
                        <textarea
                            id="ef-follow-note"
                            value={modalNote}
                            onChange={(e) => setModalNote(e.target.value)}
                            placeholder="Ej.: Ritual de 10 minutos al inicio; revisamos carga y cerramos alertas abiertas…"
                            rows={4}
                        />
                        <div className="modal-actions">
                            <button type="button" className="btn-cancel" onClick={() => setModalCase(null)}>
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn-save"
                                disabled={savingModal || !modalNote.trim()}
                                onClick={saveRegister}
                            >
                                {savingModal ? 'Guardando…' : 'Guardar y cerrar ciclo'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
